import Vue from 'vue';
import { mapGetters } from 'vuex';
import DownloadExportButton from './DownloadExportButton.vue';
import HelpIcon from './HelpIcon.vue';
import ChartUtils from 'src/utils/chart';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem.vue';
/**
 * Simple chart to render concepts from the context network.
 */
export default Vue.extend({
    components: {
        DownloadExportButton, DropdownItem, Dropdown, HelpIcon,
    },
    props: {
        model: { type: Object, default: null, required: false },
        loading: { type: Boolean, default: false },
        headerTools: { type: Boolean, default: true },
        height: { type: Number, default: 450 },
        limit: { type: Number, default: 25 }, // max number of terms to show
    },
    data() {
        return {
            contextConceptsChart: null,
            sortMode: 'frequency',
        };
    },
    computed: {
        ...mapGetters(['currentAnalysis', 'currentModel', 'currentUser']),
        concepts() {
            return this?.model?.concepts || [];
        },
        showInfluenceMode() {
            const hasInfluence = !!this.concepts.find((concept) => !!concept.influence_strength);
            return hasInfluence;
        },
        conceptValues() {
            return this.sortedConcepts.map((c) => this.sortMode === 'frequency'
                ? this.normaliseFreq(c.frequency)
                : c.influence_strength ?? 0);
        },
        help() {
            return `<p>A graph of the strongest context concepts/terms for this query.</p>`
                + `<p>If there are concepts or terms in your query, the items in this chart will be selected based on how much they are influenced by the concepts/terms in your query. Influence is a measure of the importance of a relationship between two concepts/terms (see the glossary in our Knowledge Base for more details).</p>`
                + `<p>If there is only structured data in your query, the items in this chart will be selected based on frequency.</p>`;
        },
        isInsufficientData() {
            return (this?.model?.concepts?.length || 0) === 0;
        },
        sortedConcepts() {
            return this.concepts.slice().sort((a, b) => this.sortMode === 'frequency'
                ? b.frequency - a.frequency
                : (b.influence_strength ?? 0) - (a.influence_strength ?? 0));
        },
        xAxisLabel() {
            return this.sortMode === 'frequency' ? '% of Verbatims' : 'Influence';
        }
    },
    watch: {
        model() {
            this.draw();
        },
        sortMode() {
            this.draw();
        },
    },
    mounted() {
        this.draw();
    },
    methods: {
        normaliseFreq(freq) {
            return freq / this.model.n_frames * 100;
        },
        draw() {
            if (this.sortedConcepts.length <= 0) {
                return;
            }
            if (this.contextConceptsChart) {
                this.contextConceptsChart.destroy();
                this.contextConceptsChart = null;
            }
            // Assemble (truncated) chart data
            const conceptLabels = [];
            const conceptColours = [];
            const conceptValues = this.conceptValues.slice(0, this.limit);
            for (const concept of this.sortedConcepts.slice(0, this.limit)) {
                conceptLabels.push(concept.name);
                conceptColours.push(this.currentModel.conceptColours[concept.name] || '#cbcbcb');
            }
            const chartData = {
                labels: conceptLabels,
                datasets: [{
                        label: this.xAxisLabel,
                        borderColor: 'black',
                        borderWidth: Array.apply(null, Array(conceptValues.length)).map(Number.prototype.valueOf, 0),
                        data: conceptValues,
                        maxBarThickness: 80,
                        backgroundColor: conceptColours
                    }]
            };
            // Render the chart
            const chartEl = this.$refs.contextChart;
            const chartCtx = chartEl.getContext('2d');
            this.contextConceptsChart = new Chart(chartCtx, {
                type: 'horizontalBar',
                data: chartData,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    onClick: (e, el) => {
                        if (!el || el.length === 0) {
                            return;
                        }
                        const index = el[0]._index;
                        this.$emit('concept-selected', this.sortedConcepts[index].name);
                    },
                    hover: {
                        // Pointer cursor
                        onHover: function (e) {
                            const point = this.getElementAtEvent(e);
                            e.target.style.cursor = point.length ? 'pointer' : 'default';
                        }
                    },
                    legend: {
                        display: false
                    },
                    scales: {
                        yAxes: [{
                                gridLines: {
                                    display: false,
                                    zeroLineWidth: 0,
                                    color: ChartUtils.AXIS_COLOUR,
                                    zeroLineColor: ChartUtils.AXIS_COLOUR
                                }
                            }],
                        xAxes: [{
                                scaleLabel: {
                                    display: true,
                                    labelString: this.xAxisLabel,
                                },
                                ticks: {
                                    beginAtZero: true
                                },
                                gridLines: {
                                    display: true,
                                    zeroLineWidth: 0,
                                    color: ChartUtils.AXIS_COLOUR,
                                    zeroLineColor: ChartUtils.AXIS_COLOUR
                                }
                            }]
                    },
                    tooltips: {
                        callbacks: {
                            label: (tooltipItem) => {
                                return this.sortMode === 'frequency'
                                    ? ` Frequency: ${ChartUtils.percentLabel(tooltipItem)}`
                                    : ` Influence: ${tooltipItem.xLabel.toFixed(2)}`;
                            },
                            footer: (tooltipItems) => {
                                const concept = this.sortedConcepts[tooltipItems[0].index];
                                if (this.sortMode === 'frequency' && concept.influence_strength !== undefined) {
                                    return `(Influence: ${concept.influence_strength.toFixed(2)})`;
                                }
                                if (this.sortMode === 'influence') {
                                    return `(Frequency: ${this.normaliseFreq(concept.frequency).toFixed(2)}%)`;
                                }
                            }
                        }
                    },
                }
            });
        },
        getContextChartEl() {
            return this.$refs.contextChart;
        },
        getCsvData() {
            return this.sortedConcepts.map((d) => {
                const freq = this.normaliseFreq(d.frequency);
                const v = {
                    'name': d.name,
                    '% of Verbatims': ChartUtils.formatPercent(freq),
                };
                if (d.influence_strength !== undefined) {
                    v['Influence'] = d.influence_strength;
                }
                return v;
            });
        }
    }
});
